import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'ippc';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: LucideIcon;
  loading?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary: 'bg-wood-500 text-white hover:bg-wood-700 disabled:bg-oak-300',
  secondary: 'bg-walnut-900 text-white hover:bg-ironwood disabled:bg-oak-500',
  ghost: 'bg-white text-walnut-900 border border-birch-200 hover:bg-pine-100 disabled:text-oak-300',
  danger: 'bg-danger text-white hover:bg-[#9d3f31] disabled:bg-oak-300',
  ippc: 'bg-ippc-500 text-white hover:bg-ippc-700 disabled:bg-oak-300',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-xs gap-1',
  md: 'h-9 px-3.5 text-sm gap-1.5',
  lg: 'h-10 px-4.5 text-sm gap-2',
};

const iconSizeClasses: Record<Size, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-4 w-4',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', icon: Icon, loading, children, className = '', disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      {...rest}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors disabled:cursor-not-allowed ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
    >
      {Icon ? <Icon className={iconSizeClasses[size]} /> : null}
      {children}
    </button>
  );
});

export default Button;
